import {html, HTMLTemplateResult} from "lit";
import {translate} from "lit-translate";

import {actionRegistry} from "../actions/action-registry";
import {ActionIds} from "../actions/action.types";

/**
 * Toggles the Knowledge `.sidebar__filter` drawer (`appState.knowledgeFilterOpen`,
 * read/rendered in `knowledge-overview-view.component.ts`). Shared by every
 * place that renders the filter-toggle button — see
 * `renderKnowledgeFilterToggleButton` below.
 */
export function toggleKnowledgeFilterDrawer(): void {
    void actionRegistry.execute(ActionIds.SIDEBAR_TOGGLE_KNOWLEDGE);
}

/**
 * Renders the filter-drawer toggle button.
 *
 * Stefan, 2026-08-30: the button used to live in its own near-empty
 * `.timeline-toolbar` above `knowledge-view-pane` (via
 * `ToolbarHelper.renderTimelineToolbar([knowledgeFiltersToolbarItem])`,
 * that toolbar-item file and its `.timeline-toolbar` wrapper are now
 * removed) — moved into whichever toolbar the currently-open object type
 * already has of its own, so it no longer occupies a whole toolbar row by
 * itself:
 * - `knowledge-whiteboard.component.ts`: passed as an `InjectedButton`
 *   descriptor into `<o2d-whiteboard>`'s own toolbar (that package renders
 *   injected buttons with its OWN markup, not this function — see there).
 * - `knowledge-rte.component.ts`: rendered with THIS function inside its
 *   own wrapper-level `.knowledge-rte__toolbar` (the RTE PACKAGE's internal
 *   `.o2d-rte-toolbar` only exists in edit mode, so it wasn't a viable home
 *   — the wrapper's toolbar is always rendered regardless of view/edit).
 * - `knowledge-view-pane.component.ts`: rendered with THIS function as a
 *   fallback for the two types that have no toolbar of their own at all
 *   (`image`, and the no-item empty state) — Stefan confirmed the filter
 *   drawer must stay reachable even then, not just while a
 *   whiteboard/document happens to be open.
 *
 * Markup/classes are unchanged from the removed toolbar-item file, minus the
 * `.filter-buttons.topbar-element` wrapper div it used to sit in (Stefan,
 * 2026-08-30: removed — that wrapper existed to lay out the button alongside
 * a reset button in the old standalone toolbar, which no longer applies now
 * that this button lives inside another component's own toolbar). Just the
 * bare `<button>` now — existing CSS for `button.filter` keeps applying as-is.
 */
export function renderKnowledgeFilterToggleButton(): HTMLTemplateResult {
    return html`
        <button type="button"
                class="filter buttonform-marker secondary"
                data-label="${translate('toolbar.filter')}"
                @click=${toggleKnowledgeFilterDrawer}>
            <div class="hover-effect"></div>
        </button>
    `;
}
